import { api, unwrap } from "@lumberset/api-client";
import { type StaffTicketFilters, supportQueries } from "@lumberset/support-ui";
import { queryOptions } from "@tanstack/react-query";

/** Query definitions, one per thing the panel reads. Keys are what mutations invalidate. */

export const meQuery = queryOptions({
  queryKey: ["me"],
  queryFn: () => unwrap(api.ops.me.$get()),
  staleTime: 60_000,
});

export const overviewQuery = queryOptions({
  queryKey: ["admin", "overview"],
  queryFn: () => unwrap(api.admin.overview.$get()),
});

/** The workspaces in view: everything but the archived ones. */
export const yardsQuery = queryOptions({
  queryKey: ["admin", "yards"],
  queryFn: () => unwrap(api.admin.yards.$get({ query: { view: "active" } })),
});

/** The workspaces an Admin archived, merged ones among them, newest archive first. */
export const archivedYardsQuery = queryOptions({
  queryKey: ["admin", "yards", "archived"],
  queryFn: () => unwrap(api.admin.yards.$get({ query: { view: "archived" } })),
});

/**
 * The Workspaces list, searched on the server by workspace name or by the name
 * or email of someone in it. The last answer stays while the next one loads.
 */
export const workspacesSearchQuery = (q: string) =>
  queryOptions({
    queryKey: ["admin", "yards", { q }],
    queryFn: () => unwrap(api.admin.yards.$get({ query: { q: q || undefined } })),
    placeholderData: (previous) => previous,
  });

export const billingPlansQuery = queryOptions({
  queryKey: ["admin", "billing-plans"],
  queryFn: () => unwrap(api.admin["billing-plans"].$get()),
});

export const ledgerQuery = queryOptions({
  queryKey: ["admin", "ledger"],
  queryFn: () => unwrap(api.admin.ledger.$get()),
});

export const settingsQuery = queryOptions({
  queryKey: ["admin", "settings"],
  queryFn: () => unwrap(api.admin.settings.$get()),
});

export const auditLogQuery = queryOptions({
  queryKey: ["admin", "audit-log"],
  queryFn: () => unwrap(api.admin["audit-log"].$get()),
});

export const couponsQuery = queryOptions({
  queryKey: ["admin", "coupons"],
  queryFn: () => unwrap(api.admin.coupons.$get()),
});

export const couponRedemptionsQuery = (id: string) =>
  queryOptions({
    queryKey: ["admin", "coupons", id, "redemptions"],
    queryFn: () => unwrap(api.admin.coupons[":id"].redemptions.$get({ param: { id } })),
  });

export const creditPacksQuery = queryOptions({
  queryKey: ["admin", "credit-packs"],
  queryFn: () => unwrap(api.admin["credit-packs"].$get()),
});

/** Support: the numbers on the queue, the queue itself, and one Ticket. Keys are shared with the support screens. */
export const supportStatsQuery = supportQueries.stats(api);

export const supportTicketsQuery = (filters: StaffTicketFilters) =>
  supportQueries.staffTickets(filters, api);

export const supportTicketQuery = (id: string) => supportQueries.staffTicket(id, api);

/** Every email the platform sends, with the Admin's wording beside the shipped one. */
export const emailTemplatesQuery = queryOptions({
  queryKey: ["admin", "email-templates"],
  queryFn: () => unwrap(api.admin.email.templates.$get()),
});

export const announcementsQuery = queryOptions({
  queryKey: ["admin", "announcements"],
  queryFn: () => unwrap(api.admin.email.announcements.$get()),
});

/** Reading accuracy: the tiles, recent runs, one run, and the harness scores. */
export const accuracyOverviewQuery = (days: number) =>
  queryOptions({
    queryKey: ["accuracy", "overview", days],
    queryFn: () => unwrap(api.staff.accuracy.overview.$get({ query: { days: String(days) } })),
  });

export const accuracyRunsQuery = (days: number) =>
  queryOptions({
    queryKey: ["accuracy", "runs", { days }],
    queryFn: () => unwrap(api.staff.accuracy.runs.$get({ query: { days: String(days) } })),
  });

export const accuracyRunQuery = (id: string) =>
  queryOptions({
    queryKey: ["accuracy", "runs", id],
    queryFn: () => unwrap(api.staff.accuracy.runs[":id"].$get({ param: { id } })),
  });

export const accuracyScoresQuery = queryOptions({
  queryKey: ["accuracy", "scores"],
  queryFn: () => unwrap(api.staff.accuracy.scores.$get()),
});

/** Whether Confidence works: reliability against Review, and Corrections found early. */
export const accuracyConfidenceQuery = (days: number) =>
  queryOptions({
    queryKey: ["accuracy", "confidence", days],
    queryFn: () => unwrap(api.staff.accuracy.confidence.$get({ query: { days: String(days) } })),
  });
